import React, { useState } from 'react';
import { faCheck } from '@fortawesome/free-solid-svg-icons/faCheck';
import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus';
import { faMinus } from '@fortawesome/free-solid-svg-icons/faMinus';
import { camelize } from 'inflection';

import Button, { BUTTON_SIZES, BUTTON_TYPES } from 'buyside/components/presentation/Button/button';
const { watchlistFollowableTypes } = require('buyside/util/constantsFromRails.json');

import {
  FollowIdsFragment,
  useMainNavigationCurrentUserQuery,
  useFollowItemMutation,
  useUnfollowItemMutation
} from 'interfaces/graphql-typings';

import styles from './followButtonStyles.scss';

export enum FollowableType {
  FUND = 'FUND',
  USER = 'USER',
  ENTITY = 'ENTITY',
  ESG = 'ESG',
  GLOBAL_MACRO = 'GLOBAL_MACRO',
  SECTOR = 'SECTOR',
  GEOGRAPHY = 'GEOGRAPHY'
}

const FollowButton: React.FC<{
  analyticsTag?: string;
  badge?: boolean;
  buttonDisplay?: string;
  buttonType?: BUTTON_TYPES;
  followedClassName?: string;
  id: string;
  joyrideTarget?: string;
  size?: BUTTON_SIZES;
  type: keyof typeof FollowableType;
}> = ({
  analyticsTag,
  badge,
  buttonDisplay = '',
  buttonType,
  followedClassName,
  id,
  joyrideTarget,
  size,
  type
}) => {
  const [hover, setHover] = useState(false);
  const [followed, setFollowed] = useState<boolean>();

  const key = `followed${watchlistFollowableTypes[type]}Ids` as keyof Omit<
    FollowIdsFragment,
    '__typename'
  >;

  const { data } = useMainNavigationCurrentUserQuery({
    onCompleted: data => {
      if (!data?.currentUser) return;
      setFollowed(data.currentUser[key].indexOf(id) > -1);
    }
  });

  const [handleFollow, { loading: followLoading }] = useFollowItemMutation({
    variables: { followId: id, type },
    update(cache, { data }) {
      cache.modify({
        id: `${camelize(type.toLowerCase())}:${id}`,
        fields: {
          followersCount: prevCount => prevCount + 1,
          newFollowersCount: prevCount => prevCount + 1
        }
      });

      const user = data?.followObject?.user;
      if (!user) return;
      const newIds = user[key];
      setFollowed(true);
      cache.modify({
        id: cache.identify({ id: user.id, __typename: 'User' }),
        fields: {
          [key]: () => newIds
        }
      });
    }
  });

  const [handleUnfollow, { loading: unfollowLoading }] = useUnfollowItemMutation({
    variables: { followId: id, type },
    update(cache, { data }) {
      cache.modify({
        id: `${camelize(type.toLowerCase())}:${id}`,
        fields: {
          followersCount: prevCount => prevCount - 1,
          newFollowersCount: prevCount => prevCount - 1
        }
      });

      const user = data?.unfollowObject?.user;
      if (!user) return;
      const newIds = user[key];
      setFollowed(false);
      cache.modify({
        id: cache.identify({ id: user.id, __typename: 'User' }),
        fields: {
          [key]: () => newIds
        }
      });
    }
  });

  return (
    <span onMouseEnter={(): void => setHover(true)} onMouseLeave={(): void => setHover(false)}>
      {followed ? (
        <Button
          id={`unfollow-${type}-${id}`}
          className={badge ? styles.badge : followedClassName}
          text={hover ? `Unfollow ${buttonDisplay}` : `Following ${buttonDisplay}`}
          onClick={handleUnfollow}
          type={
            hover && buttonType !== BUTTON_TYPES.LINK
              ? BUTTON_TYPES.DANGER
              : badge
              ? BUTTON_TYPES.MAJOR
              : buttonType || BUTTON_TYPES.PRIMARY
          }
          faIcon={hover ? faMinus : badge ? faCheck : null}
          isLoading={followLoading || unfollowLoading}
          size={size || (badge && BUTTON_SIZES.MICRO) || BUTTON_SIZES.MEDIUM}
          analyticsTag={analyticsTag}
          joyrideTarget={joyrideTarget}
        />
      ) : (
        <Button
          className={badge && styles.badge}
          id={`follow-${type}-${id}`}
          text={`Follow ${buttonDisplay}`}
          onClick={handleFollow}
          type={buttonType || BUTTON_TYPES.SECONDARY}
          faIcon={faPlus}
          size={size || (badge && BUTTON_SIZES.MICRO) || BUTTON_SIZES.MEDIUM}
          isLoading={followLoading || unfollowLoading || !data}
          analyticsTag={analyticsTag}
          joyrideTarget={joyrideTarget}
        />
      )}
    </span>
  );
};

export default FollowButton;
